// src/pages/end-user-license-agreement.jsx
// Tailwind-styled EULA with anchors and easy-to-edit constants.

import React from "react";

const META = {
  COMPANY_NAME: "Afterparty BotLabs",
  COMPANY_SHORT: "Afterparty",
  SITE_URL: "https://afterpartylabs.xyz",
  CONTACT_EMAIL: "support@afterpartylabs.xyz",
  GOVERNING_LAW: "State of California, U.S.A.",
  CAP_AMOUNT: "$50.00",
  LAST_UPDATED: "January 20, 2026",
};

export default function EndUserLicenseAgreement() {
  return (
    <div className="bg-gradient-to-br from-purple-900 to-blue-900 min-h-full text-white">
      <div className="max-w-5xl mx-auto px-4 py-16">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-2">
          End User License Agreement (EULA)
        </h1>
        <p className="text-sm text-gray-300 mb-8">
          Last updated: {META.LAST_UPDATED}
        </p>

        {/* Intro / Acceptance */}
        <section id="acceptance" className="space-y-4">
          <p className="text-gray-100 leading-relaxed">
            BY CLICKING “ADD TO DISCORD,” “INVITE,” OR OTHERWISE INSTALLING,
            ENABLING, OR USING ANY BOT OR SERVICE PROVIDED BY {META.COMPANY_NAME}{" "}
            ({META.COMPANY_SHORT}), YOU (OR THE ENTITY YOU REPRESENT)
            (“<strong>Licensee</strong>”) AGREE TO BE BOUND BY THIS END USER
            LICENSE AGREEMENT (“<strong>Agreement</strong>”). IF YOU DO NOT
            AGREE, DO NOT INSTALL OR USE THE BOT.
          </p>
          <p className="text-gray-200">
            If you add the bot to your server, you represent and warrant that
            you are authorized to bind the Licensee to this Agreement.
          </p>
        </section>

        {/* Table of Contents */}
        <nav className="my-8">
          <h2 className="text-xl font-bold mb-3">Contents</h2>
          <ul className="grid gap-2 text-blue-200">
            {[
              ["Grant of License", "grant"],
              ["Services & Fees", "fees"],
              ["Licensee Responsibilities", "responsibilities"],
              ["Third-Party Services", "thirdparty"],
              ["Restrictions", "restrictions"],
              ["User Data & Privacy", "userdata"],
              ["Termination", "termination"],
              ["Warranty Disclaimers", "warranty"],
              ["Limitation of Liability", "liability"],
              ["Publicity / Identification", "publicity"],
              ["Miscellaneous", "misc"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <hr className="border-white/10 my-8" />

        {/* Grant of License */}
        <section id="grant" className="space-y-3">
          <h2 className="text-2xl font-bold">1. Grant of License</h2>
          <p className="text-gray-200 leading-relaxed">
            Subject to Licensee’s continuous compliance with this Agreement,{" "}
            {META.COMPANY_SHORT} grants Licensee a personal, non-exclusive,
            non-transferable, non-sublicensable, revocable, royalty-free license
            to install and use the applicable {META.COMPANY_SHORT} Discord bot(s)
            (the “<strong>Bot</strong>”) on authorized Discord servers solely to:
            (a) administer and moderate servers; (b) stream or control audio
            content where permitted; (c) deliver updates, game or account
            information; and (d) run automations and community features approved
            by {META.COMPANY_SHORT}. {META.COMPANY_SHORT} retains all ownership
            rights in and to the Bot.
          </p>
        </section>

        {/* Services & Fees */}
        <section id="fees" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">2. Services & Fees</h2>
          <p className="text-gray-200">
            The Services may include free and/or paid features. {META.COMPANY_SHORT}{" "}
            reserves the right to introduce, modify, or require payment for any
            portion of the Services. If you use the Services for or on behalf of
            an organization or as part of commercial activity, contact us at{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="underline">
              {META.CONTACT_EMAIL}
            </a>{" "}
            for enterprise terms.
          </p>
        </section>

        {/* Responsibilities */}
        <section id="responsibilities" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">3. Licensee Responsibilities</h2>
          <ul className="list-disc pl-6 text-gray-200 space-y-2">
            <li>
              Ensure your use of the Site and Bots complies with applicable
              laws, Discord’s Terms, and third-party rights.
            </li>
            <li>
              You are responsible for server configurations, permissions,
              content, and user conduct on servers you control.
            </li>
            <li>
              You agree to {META.COMPANY_SHORT}’s{" "}
              <a href="/tos" className="underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className="underline">
                Privacy Policy
              </a>{" "}
              as updated from time to time.
            </li>
          </ul>
        </section>

        {/* Third Party Services */}
        <section id="thirdparty" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">4. Third-Party Services</h2>
          <p className="text-gray-200">
            The Bot may interact with or rely on third-party services and APIs
            (e.g., Discord, music or game data providers). {META.COMPANY_SHORT}{" "}
            is not responsible for such services, their availability, or their
            terms. Licensee is solely responsible for obtaining rights to use
            such services and complying with their terms.
          </p>
        </section>

        {/* Restrictions */}
        <section id="restrictions" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">5. Restrictions</h2>
          <p className="text-gray-200">
            Licensee will not, and will not permit any third party to:
          </p>
          <ul className="list-disc pl-6 text-gray-200 space-y-2">
            <li>
              modify, reverse engineer, decompile, or attempt to derive source
              code or underlying ideas/algorithms of the Bot (except to the
              limited extent prohibited by law);
            </li>
            <li>
              rent, lease, lend, sell, sublicense, or provide the Bot for
              timesharing/service bureau purposes;
            </li>
            <li>
              remove or alter any proprietary notices or branding;
            </li>
            <li>
              publish benchmarks or comparative analyses of the Bot without
              prior written consent;
            </li>
            <li>
              use the Bot in violation of laws, regulations, or third-party
              terms, including intellectual-property and export-control rules.
            </li>
          </ul>
        </section>

        {/* User Data */}
        <section id="userdata" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">6. User Data & Privacy</h2>
          <p className="text-gray-200">
            “<strong>User Data</strong>” means data submitted or generated in
            connection with Licensee’s use of the Bot. As between the parties,
            Licensee owns all right, title, and interest in User Data. Licensee
            is responsible for accuracy, legality, and obtaining permissions for
            User Data. {META.COMPANY_SHORT} will use commercially reasonable
            efforts to maintain security and integrity of User Data and may
            internally use and modify User Data to operate, maintain, secure,
            and improve the Bot and to create de-identified{" "}
            <strong>Aggregated Anonymous Data</strong> for analytics,
            improvement, promotion, and benchmarking.
          </p>
          <p className="text-gray-200">
            For details on collection and processing, see our{" "}
            <a href="/privacy" className="underline">
              Privacy Policy
            </a>
            .
          </p>
        </section>

        {/* Termination */}
        <section id="termination" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">7. Termination</h2>
          <p className="text-gray-200">
            {META.COMPANY_SHORT} may suspend or terminate this Agreement and
            Licensee’s access to the Bot upon notice for any uncured breach, or
            immediately for serious violations. Upon termination, you must cease
            all use and remove the Bot from your servers. Sections intended to
            survive (including ownership, disclaimers, limitations of liability,
            and miscellaneous) will continue in effect.
          </p>
        </section>

        {/* Warranty */}
        <section id="warranty" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">8. Warranty Disclaimers</h2>
          <p className="text-gray-200">
            THE BOT AND SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.”{" "}
            {META.COMPANY_SHORT} AND ITS LICENSORS DISCLAIM ALL WARRANTIES,
            WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT, AND DO NOT GUARANTEE UNINTERRUPTED OR ERROR-FREE
            OPERATION OR RESULTS.
          </p>
        </section>

        {/* Liability */}
        <section id="liability" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">9. Limitation of Liability</h2>
          <p className="text-gray-200">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL{" "}
            {META.COMPANY_SHORT} OR ITS LICENSORS BE LIABLE FOR INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES,
            OR FOR LOST PROFITS, LOST DATA, OR INTERRUPTION/INABILITY TO USE.
            IN NO EVENT WILL THE AGGREGATE LIABILITY OF{" "}
            {META.COMPANY_SHORT} EXCEED {META.CAP_AMOUNT}.
          </p>
        </section>

        {/* Publicity / Identification */}
        <section id="publicity" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">10. Publicity / Identification</h2>
          <p className="text-gray-200">
            Licensee permits {META.COMPANY_SHORT} to reference Licensee’s server
            name and logo to identify Licensee as a user of the Bot (e.g., on
            {` ${META.SITE_URL}`} or marketing materials). Licensee may opt out
            by emailing{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="underline">
              {META.CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>

        {/* Misc */}
        <section id="misc" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">11. Miscellaneous</h2>
          <ul className="list-disc pl-6 text-gray-200 space-y-2">
            <li>
              This Agreement may not be assigned by Licensee without{" "}
              {META.COMPANY_SHORT}’s prior written consent.{" "}
              {META.COMPANY_SHORT} may assign without consent.
            </li>
            <li>
              If any provision is found unenforceable, it will be limited to the
              minimum extent necessary, and the remainder will remain in force.
            </li>
            <li>
              This Agreement is governed by the laws of the {META.GOVERNING_LAW},
              excluding its conflict-of-law rules. The U.N. Convention on
              Contracts for the International Sale of Goods does not apply.
            </li>
            <li>
              This Agreement (together with policies referenced herein)
              constitutes the entire agreement between the parties regarding the
              subject matter and supersedes prior or contemporaneous agreements.
              Amendments must be in writing and signed by both parties.
            </li>
            <li>
              Notices may be sent to {META.CONTACT_EMAIL}. The substantially
              prevailing party in an action to enforce this Agreement may
              recover reasonable attorneys’ fees and costs.
            </li>
          </ul>
        </section>

        {/* Contact */}
        <section id="contact" className="space-y-3 mt-8">
          <h2 className="text-2xl font-bold">12. Contact</h2>
          <p className="text-gray-200">
            Questions about this Agreement? Email{" "}
            <a href={`mailto:${META.CONTACT_EMAIL}`} className="underline">
              {META.CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}